import '../../library/lists/d-list-section.js';
import '../../library/lists/d-list-section-item.js';
import { displayName, uuid } from 'src/utilities/text';
import { t } from 'src/i18n';
import { css, html, nothing } from 'lit';
import { PageContent } from 'src/pages/page-content';
import '../../library/fields/d-expansion';
import '../../library/fields/d-activity-indicator';
import type { MeetingsPageMeeting, MeetingsPageViewModel } from './meetings-page-view-model';
import { customElement, property, state } from 'lit/decorators.js';
import { LocalDate } from 'src/utilities/local-date';
import { DNewEmployeeDialog } from 'src/layout/parts/d-new-employee-dialog';
import { features } from '../../store/selectors/features/features';
import { getStore } from '../../store';
import type { CreateEntityInput } from '../../layout/parts/create-entity-input';
import type { ActionInput } from '../../library/elements/action-input';

/**
 *
 */
@customElement('d-meetings-page-content')
export class DMeetingsPageContent extends PageContent<MeetingsPageViewModel> {
  static readonly styles = [
    ...super.styles,
    css`
      .more {
        padding-left: var(--listPaddingLeft);
        padding-top: 12px;
        padding-bottom: 12px;
        color: var(--themeColor);
        cursor: pointer;
        mix-blend-mode: multiply;
      }
      @media (hover: hover) {
        .more:hover {
          color: black;
        }
      }
      .meeting-wrapper:last-child {
        margin-bottom: -1px;
      }
      .meeting {
        padding: 12px 0 10px 0;
        border-bottom: 1px solid var(--borderColorOnGray);
      }
      .meeting-wrapper:hover .meeting {
        background-color: hsla(0, 0%, 100%, 0.2);
      }
      .meeting-header {
        display: flex;
        flex-direction: row-reverse;
        flex-wrap: wrap;
        align-items: baseline;
        justify-content: space-between;
      }
      .meeting-wrapper[suggested] .meeting-header d-label {
        opacity: 0.6;
      }
      :host([write-access]) .meeting-wrapper[suggested] {
        cursor: pointer;
      }
      .meeting-wrapper:hover .meeting-header d-label {
        opacity: 1;
      }
      .meeting-header > *:last-child {
        flex-grow: 1;
        margin-right: 12px;
        margin-bottom: 2px;
      }
      .meeting-header .actions {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        margin: -6px -8px;
      }
      .meetings {
        position: relative;
        margin-bottom: -1px;
      }
      .list-fade-out {
        position: absolute;
        right: 0;
        /* bottom must match the height of .in-list-action (padding + one line d-action mini) */
        bottom: 33px;
        left: 0;
        height: 60px;
        background: linear-gradient(180deg, var(--backgroundGrayTransparent) 0%, var(--backgroundGray) 100%);
        pointer-events: none;
      }
      .in-list-action {
        padding: 5px 0;
      }
      .in-list-action d-action {
        margin-left: -8px;
      }
      .meeting-prev-next {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        margin-top: 4px;
      }
      .meeting-prev-next > * {
        margin-right: 12px;
      }
      d-activity-indicator {
        margin: 8px 0;
      }
      a.prev-next-text {
        color: var(--linkColorGray);
        text-decoration: none;
      }
      a.prev-next-text[disabled] {
        color: black;
        cursor: default;
        pointer-events: none;
      }
      a.prev-next-text[classified] {
        padding-left: 20px;
        background-image: url(/images/locked.svg);
        background-repeat: no-repeat;
        background-position: -4px -4px;
        background-size: 24px;
      }
      span.prev-next-text {
        font-style: italic;
        font-weight: 200;
      }
      .prev-next-text .label {
        padding-right: 2px;
        font-weight: 500;
        font-style: normal;
      }
      @media only screen and (min-width: 600px) {
        .meeting-wrapper {
          padding-left: var(--listPaddingLeft);
        }
        .meeting-wrapper[classified] .meeting-header {
          padding-left: 0;
        }
        .in-list-action {
          margin-left: var(--listPaddingLeft);
        }
      }
      @media (hover: hover) {
        .meeting a.prev-next-text:hover {
          color: var(--themeColorDarkerOne);
        }
        .meeting a.prev-next-text[disabled],
        .meeting a.prev-next-text[disabled]:hover {
          color: black;
          cursor: default;
        }
      }
    `,
  ];
  @property({ type: Number })
  limitListOver = 5;
  @property({ type: Number })
  limitedListLength = 3;
  @property({ type: Boolean })
  pastMeetingsShowAll = false;
  @property({ type: Boolean })
  suggestedMeetingsShowAll = false;
  @property({ type: Boolean })
  activeEmployeeInterviewsShowAll = false;
  @property({ type: Boolean })
  terminatedEmployeeInterviewsShowAll = false;

  @state()
  private futureMeetingsShowAll = true;

  @state()
  private visibleMeetingIds = new Set<string>();

  // Note: Using manual IntersectionObserver instead of @lit-labs/observers IntersectionController
  // because IntersectionController is designed to observe the host element, not dynamic children.
  // With 40+ dynamic meeting elements, manual observer setup in updated() is the appropriate pattern.
  private intersectionObserver?: IntersectionObserver;
  private observedElements = new WeakSet<Element>();

  private get indicatorYears(): number[] {
    const thisYear = this.pageView.today.year();
    return [thisYear - 1, thisYear, thisYear + 1];
  }

  connectedCallback() {
    super.connectedCallback();
    this.setupIntersectionObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.intersectionObserver?.disconnect();
  }

  private setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        let hasChanges = false;
        entries.forEach((entry) => {
          const meetingId = entry.target.getAttribute('data-meeting-id');
          if (meetingId) {
            if (entry.isIntersecting) {
              if (!this.visibleMeetingIds.has(meetingId)) {
                this.visibleMeetingIds.add(meetingId);
                hasChanges = true;
              }
            } else {
              if (this.visibleMeetingIds.has(meetingId)) {
                this.visibleMeetingIds.delete(meetingId);
                hasChanges = true;
              }
            }
          }
        });
        if (hasChanges) {
          this.requestUpdate();
        }
      },
      {
        root: null,
        rootMargin: '100px', // Start rendering 100px before entering viewport
        threshold: 0,
      },
    );
  }

  updated(changedProperties: Map<PropertyKey, unknown>) {
    super.updated(changedProperties);
    // Observe all meeting wrappers that haven't been observed yet
    this.shadowRoot?.querySelectorAll('.meeting-wrapper[data-meeting-id]').forEach((el) => {
      if (!this.observedElements.has(el)) {
        this.intersectionObserver?.observe(el);
        this.observedElements.add(el);
      }
    });
  }

  handleWrapperClick(e, _templateHref: string, meeting: MeetingsPageMeeting) {
    if (meeting.suggested) {
      this.onCreateOccurrence(e, meeting);
    }
  }

  firstItems(items) {
    return items.filter((_item, index) => {
      return index < this.limitedListLength;
    });
  }

  lastItems(items) {
    return items.filter((_item, index) => {
      return index > this.limitedListLength - 1;
    });
  }

  listActions(listName): ActionInput[] {
    const actions: ActionInput[] = [];
    if (listName === 'futureMeetings' && this.pageView.writeAccess) {
      actions.push({
        name: t('meetings.addNewMeeting'),
        action: 'create-meeting',
        slot: 'top-right',
      });
    } else if (listName === 'terminatedEmployeeInterviews') {
      if (this.terminatedEmployeeInterviewsShowAll) {
        actions.push({
          name: t('meetings.hide'),
          action: 'toggleShowAll',
          slot: 'top-right',
        });
      } else {
        actions.push({
          name: t('meetings.show'),
          action: 'toggleShowAll',
          slot: 'top-right',
        });
      }
    } else if (listName === 'activeEmployeeInterviews' && this.pageView.writeAccess) {
      actions.push({
        name: t('meetings.addNewPersonAction'),
        action: 'create-employee',
        slot: 'top-right',
      });
    }
    return actions;
  }

  async handeListHeaderAction(e, listName) {
    e.preventDefault();
    e.stopPropagation();
    if (e.detail === 'toggleShowTerminated') {
      this.terminatedEmployeeInterviewsShowAll = !this.terminatedEmployeeInterviewsShowAll;
    }
    if (e.detail === 'toggleShowAll') {
      this[listName + 'ShowAll'] = !this[listName + 'ShowAll'];
    } else if (e.detail === 'create-meeting') {
      const id = uuid();
      const input: CreateEntityInput = {
        entityType: 'meetingOccurrences',
        entityUuid: id,
        instance: LocalDate.now().plusDays(1).toString(),
        targetUrl: '/account/' + this.pageView.organizationId + '/9772/meetingOccurrences/' + id + '?edit',
      };
      this.dispatchEvent(
        new CustomEvent<CreateEntityInput>('create-entity', {
          bubbles: true,
          composed: true,
          detail: input,
        }),
      );
    }
    if (e.detail === 'create-employee') {
      await DNewEmployeeDialog.open({
        userEmails: this.pageView.userEmails,
      });
    }
  }

  activities(occurrences) {
    return occurrences.map((o) => {
      let href = this.pageView.href + 'meetingOccurrences/' + o.uuid;
      if (!o.isConfirmedEntity) {
        href += '?edit';
      }
      let classes = '';
      if (o.classified) {
        classes += ' classified';
      }
      if (o.restricted) {
        classes += ' restricted';
      }
      if (o.meetingStatus === 'NOTICE_SENT' || o.meetingStatus === 'REPORT_WRITTEN') {
        classes += ' meeting-initialized';
      }
      return { date: o.date, href, classes };
    });
  }

  renderMeeting({ meeting, listName }: { meeting: MeetingsPageMeeting; listName: string }) {
    const templateHref = this.pageView.href + 'meetings/' + meeting.uuid;
    const optionalMeetingCalendarAction =
      meeting.prevDate || meeting.nextDate
        ? html` <d-action gray href="${templateHref}" theme-page mini>${t('meetings.meetingCalendar')}</d-action> `
        : nothing;
    const addMeetingLabel = meeting.suggested ? html`${t('meetings.takeIntoUse')}` : html`${t('meetings.add')}`;
    const optionalAddMeetingAction =
      this.pageView.writeAccess && listName !== 'terminatedEmployeeInterviews'
        ? html`
            <d-action gray @click=${(e: CustomEvent) => this.onCreateOccurrence(e, meeting)} theme-page mini
              >${addMeetingLabel}</d-action
            >
          `
        : nothing;
    const previousMeetingLink = meeting.prevText
      ? html`
          <a
            class="prev-next-text"
            ?disabled="${meeting.prevRestricted}"
            ?classified="${meeting.prevClassified}"
            href="${meeting.prevHref}"
          >
            <span class="label">${t('meetings.previous')}</span>
            ${meeting.prevText}
          </a>
        `
      : html`
          <span class="prev-next-text">
            <span class="label">${t('meetings.previous')}</span>
            ${t('meetings.noPrevious')}
          </span>
        `;
    const nextMeetingLink = meeting.nextText
      ? html`
          <a
            class="prev-next-text"
            ?disabled="${meeting.nextRestricted}"
            ?classified="${meeting.nextClassified}"
            href="${meeting.nextHref}"
          >
            <span class="label">${t('meetings.next')}</span>
            ${meeting.nextText}
          </a>
        `
      : html`
          <span class="prev-next-text">
            <span class="label">${t('meetings.next')}</span>
            ${t('meetings.noUpcoming')}
          </span>
        `;
    const isVisible = this.visibleMeetingIds.has(meeting.uuid);
    const optionalActivityIndicator =
      isVisible && !meeting.suggested
        ? html`
            <d-activity-indicator
              .years=${this.indicatorYears}
              .activities=${this.activities(meeting.occurrences)}
            ></d-activity-indicator>
          `
        : nothing;
    const optionalNextPreviousLinks = !meeting.suggested
      ? html`
          ${optionalActivityIndicator}
          <div class="meeting-prev-next">
            <div>${previousMeetingLink}</div>
            ${listName !== 'pastMeetings' ? html` <div>${nextMeetingLink}</div> ` : nothing}
          </div>
        `
      : nothing;
    return html`
      <div
        class="meeting-wrapper"
        data-meeting-id="${meeting.uuid}"
        ?suggested="${meeting.suggested}"
        @click=${(e) => this.handleWrapperClick(e, templateHref, meeting)}
      >
        <div class="meeting">
          <div class="meeting-header">
            <div class="actions">${optionalMeetingCalendarAction} ${optionalAddMeetingAction}</div>
            <div class="meeting-name">
              <d-label big allow-wrap .label=${displayName(meeting.name, t('meetings.meetingWithoutName'))}></d-label>
            </div>
          </div>
          ${optionalNextPreviousLinks}
        </div>
      </div>
    `;
  }

  renderMeetingsListIfNotEmpty(label: string, sublabel: string, listName: string, meetings: MeetingsPageMeeting[]) {
    if (meetings.length === 0) {
      return nothing;
    } else {
      return this.renderMeetingsList(label, sublabel, listName, meetings);
    }
  }

  renderMeetingsList(label: string, sublabel: string, listName: string, meetings: MeetingsPageMeeting[]) {
    const optionalListHeader = label
      ? html`<d-list-header
          label="${label}"
          sublabel="${sublabel}"
          icon="meetings"
          theme-page
          bordered
          style="top: ${this.contentStickyTop - 1}px"
          .actions=${this.listActions(listName)}
          @action=${(e) => this.handeListHeaderAction(e, listName)}
        ></d-list-header>`
      : nothing;
    const showAll = this[listName + 'ShowAll'];
    return html` ${optionalListHeader}
      <div class="meetings">
        ${meetings.length > this.limitListOver
          ? html`
              ${this.firstItems(meetings).map((meeting: MeetingsPageMeeting) => {
                return html` ${this.renderMeeting({ meeting: meeting, listName })} `;
              })}
              <d-expansion .opened=${showAll}>
                ${this.lastItems(meetings).map((meeting: MeetingsPageMeeting) => {
                  return html` ${this.renderMeeting({ meeting: meeting, listName: listName })} `;
                })}
              </d-expansion>
              ${showAll ? nothing : html`<div class="list-fade-out"></div>`}
              <div class="in-list-action">
                <d-action mini theme-page @click=${() => (this[listName + 'ShowAll'] = !showAll)}>
                  ${showAll ? t('meetings.showFewer') : t('meetings.showAll')}
                </d-action>
              </div>
            `
          : html`
              ${meetings.map((meeting: MeetingsPageMeeting) => {
                return html` ${this.renderMeeting({ meeting: meeting, listName: listName })} `;
              })}
            `}
      </div>`;
  }

  renderMeetingsListIfNotEmptyAllHidden(
    label: string,
    sublabel: string,
    listName: string,
    meetings: MeetingsPageMeeting[],
  ) {
    const optionalListHeader = label
      ? html`<d-list-header
          label="${label}"
          sublabel="${sublabel}"
          icon="meetings"
          theme-page
          bordered
          style="top: ${this.contentStickyTop - 1}px"
          .actions=${this.listActions(listName)}
          @action=${(e) => this.handeListHeaderAction(e, listName)}
        ></d-list-header>`
      : nothing;
    if (meetings.length === 0) {
      return nothing;
    } else {
      return html`
        ${optionalListHeader}
        <d-expansion .opened=${this[listName + 'ShowAll']}>
          <div class="meetings">
            ${meetings.map((meeting: MeetingsPageMeeting) => {
              return html` ${this.renderMeeting({ meeting: meeting, listName: listName })} `;
            })}
          </div>
        </d-expansion>
      `;
    }
  }

  renderContent() {
    // Check features
    const state = getStore().getState();
    const organization = state.organization;
    const hasMeetings = organization?.features?.includes('MEETINGS');
    const hasPerformanceReview = organization?.features?.includes('PERFORMANCE_REVIEW');

    // Show promo section if neither feature is enabled
    if (!hasMeetings && !hasPerformanceReview) {
      if (this.pageView.promoData) {
        return html` <d-promo-section
          theme-page
          .employeesCount=${this.pageView.employeesCount}
          .specialTerms=${this.pageView.specialTerms}
          .promoData=${this.pageView.promoData}
          .features=${features}
          .canUpgrade=${this.writeAccess}
        ></d-promo-section>`;
      }
    }

    // Render content based on features in correct order
    return html`
      ${hasMeetings
        ? html`
            ${this.renderMeetingsList(
              t('meetings.upcomingMeetings'),
              '',
              'futureMeetings',
              this.pageView.futureMeetings,
            )}
            ${this.renderMeetingsListIfNotEmpty(
              t('meetings.pastMeetings'),
              '',
              'pastMeetings',
              this.pageView.pastMeetings,
            )}
          `
        : nothing}
      ${hasPerformanceReview
        ? html`
            ${this.renderMeetingsListIfNotEmpty(
              t('meetings.interviewsListTitle'),
              '',
              'activeEmployeeInterviews',
              this.pageView.activeEmployeeInterviews,
            )}
            ${this.renderMeetingsListIfNotEmptyAllHidden(
              t('meetings.interviewsListTitle'),
              t('meetings.terminatedStaff'),
              'terminatedEmployeeInterviews',
              this.pageView.terminatedEmployeeInterviews,
            )}
          `
        : nothing}
      ${hasMeetings
        ? html`
            ${this.renderMeetingsListIfNotEmpty(
              t('meetings.suggestedMeetings'),
              '',
              'suggestedMeetings',
              this.pageView.suggestedMeetings,
            )}
          `
        : nothing}
    `;
  }

  private onCreateOccurrence(e: CustomEvent, meeting: MeetingsPageMeeting) {
    e.stopPropagation();
    if (this.pageView.writeAccess) {
      const id = uuid();
      let templateMeetingOccurrenceUuid: string | undefined = undefined;
      if (meeting.meetingUuid === undefined && meeting.employeeUuid === undefined) {
        templateMeetingOccurrenceUuid = meeting.uuid;
      }
      const input: CreateEntityInput = {
        entityType: 'meetingOccurrences',
        entityUuid: id,
        meetingUuid: meeting.meetingUuid,
        employeeUuid: meeting.employeeUuid,
        templateMeetingOccurrenceUuid: templateMeetingOccurrenceUuid,
        instance: LocalDate.now().toString(),
        targetUrl: '/account/' + this.pageView.organizationId + '/9772/meetingOccurrences/' + id + '?edit',
      };
      this.dispatchEvent(
        new CustomEvent<CreateEntityInput>('create-entity', {
          bubbles: true,
          composed: true,
          detail: input,
        }),
      );
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-meetings-page-content': DMeetingsPageContent;
  }
}
